import { Axial } from '../map/HexCoordinates';

/**
 * What actually stands on the map.
 *
 * Buildings are **entities, not a layer of the terrain**. A layer is dense —
 * one value for every cell of the world — and could carry only a kind, where a
 * building has an owner, a level and a store of its own. So they travel the way
 * players do, and the split follows the one this project already makes: what
 * stands still comes over HTTP with the ground it stands on, and the moment one
 * goes up comes over the socket.
 */

export interface Placed extends Axial {
  id: string;
  /** The catalogue id, as `catalogue.json` numbers them. */
  kind: number;
  level: number;
  owner: string;
  ownerName: string;
  /** Ród właściciela, jeśli go nadał — „Własność rodu …" pod nazwą. */
  house?: string;
  /** Wersja sztandaru właściciela — stoi na budynku w punkcie `banner` bryły. */
  banner?: number;
  /**
   * Seconds of building left when the server said so, zero when it is finished.
   *
   * A countdown rather than a moment: a browser's clock can be minutes off the
   * server's, and a deadline read against the wrong one draws a bar that is
   * already full or never fills. The client counts down from this with its own
   * timer — the same arrangement the day/night phase uses.
   */
  readyIn: number;
  /** How long this level takes in total, so the bar has a denominator. */
  takes: number;
  /**
   * Szczebel, na który ten budynek się właśnie wspina.
   *
   * Obecny wyłącznie w trakcie rozbudowy — `level` mówi wtedy, co **stoi**, bo
   * obóz podnoszony do wioski jest obozem, dopóki czas nie upłynie. Klient
   * odlicza `readyIn` własnym zegarem i po zerze podmienia bryłę u siebie, więc
   * musi wiedzieć nie tylko, ile zostało, ale i czym to będzie.
   */
  raisingTo?: number;
  /**
   * W którą stronę patrzy: numer krawędzi z drogą **z chwili stawiania**.
   *
   * Nieobecne, gdy nikt tego nie ustalił — wtedy obrót losuje się tutaj,
   * z identyfikatora. Liczone przez serwer i zapisane przy budynku, bo droga
   * położona później nie ma obracać stojącego już domu.
   */
  facing?: number;
  /**
   * Oś, wzdłuż której leżą skrzydła: 0, 1 albo 2 — krawędź `axis`
   * i przeciwległa `axis + 3`, jak przy moście.
   *
   * Nieobecna u budynku jednopolowego, bo tam nie ma o co pytać. Ile pól po
   * każdej stronie, mówi `wings` w katalogu; ta liczba mówi tylko, w którą
   * stronę są odłożone.
   */
  axis?: number;
  /**
   * Nazwa osady, gdy budynek nią jest.
   *
   * Nieobecna na obozie i na wszystkim, co osadą nie jest — a nie pusta:
   * „nie ma nazwy" i „nazywa się niczym" to dwa różne stany.
   */
  name?: string;
  /** Czy jedyna zmiana nazwy została już wykorzystana. */
  renamed: boolean;
}

/** What stands within this many rings of chunks of a cell. */
export async function loadPlaced(api: string, at: Axial, radius: number): Promise<Placed[]> {
  const response = await fetch(
    `${api}/api/world/buildings/at?q=${at.q}&r=${at.r}&radius=${radius}`
  );
  if (!response.ok) {
    throw new Error(`budynki: ${response.status}`);
  }
  return (await response.json()) as Placed[];
}

/** Why a placement was refused, in the server's words. */
export interface Refused {
  code: string;
  message: string;
}

/**
 * Put one up.
 *
 * The server decides — every rule the client draws with is checked again there,
 * and this is the answer that counts.
 */
export async function placeBuilding(
  api: string,
  token: string,
  kind: string,
  at: Axial,
  /**
   * Oś skrzydeł, u budynku, który je ma — wymagana wtedy i bez znaczenia poza
   * tym. Linia trzech pól ma trzy położenia i wybiera je stawiający.
   */
  axis?: number
): Promise<Placed | Refused> {
  const response = await fetch(`${api}/api/world/build`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind, q: at.q, r: at.r, axis }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as Placed) : (body as Refused);
}

/**
 * Przełóż surowiec z sakwy do budynku.
 *
 * Druga połowa ruchu, którego pierwszą jest zbiór — i osobna trasa, bo osobne
 * są pozwolenia: budynek ma flagę `accepts` niezależną od `dispenses`.
 */
export async function stockBuilding(
  api: string,
  token: string,
  building: string,
  material: string,
  amount: number
): Promise<{ moved: number } | Refused> {
  const response = await fetch(`${api}/api/world/stock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ building, material, amount }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as { moved: number }) : (body as Refused);
}

/**
 * Wyciągnij surowiec z własnego budynku do sakwy — odwrotność `stockBuilding`.
 *
 * Z każdego własnego budynku, także z osady; nie z placu budowy. Serwer nalicza
 * najpierw produkcję, więc wyjąć można i to, co przybyło od ostatniego odczytu.
 */
export async function withdrawBuilding(
  api: string,
  token: string,
  building: string,
  material: string,
  amount: number
): Promise<{ moved: number } | Refused> {
  const response = await fetch(`${api}/api/world/withdraw`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ building, material, amount }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as { moved: number }) : (body as Refused);
}

/** What a building looks like after being raised a rung. */
export interface Raised {
  building: Placed;
  label?: string;
}

/**
 * Podnieś budynek o szczebel.
 *
 * Surowce schodzą z **magazynu budynku**, nie z sakwy — dlatego istnieje
 * `stockBuilding` i dlatego obóz przyjmuje surowce.
 */
export async function upgradeBuilding(
  api: string,
  token: string,
  building: string
): Promise<Raised | Refused> {
  const response = await fetch(`${api}/api/world/upgrade`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ building }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as Raised) : (body as Refused);
}

/** What a collection moved out of a building. */
export interface Collected {
  building: string;
  taken: { material: string; amount: number }[];
}

/**
 * Empty one of your buildings into your purse.
 *
 * A request like a placement, and refused the same way: the server settles what
 * the building has made, moves it, and says so. Nothing is deducted here — what
 * the store shows is this client counting up from what it was last told, and the
 * `purse` and `estate` messages that follow are what correct it.
 */
export async function harvestBuilding(
  api: string,
  token: string,
  building: string
): Promise<Collected | Refused> {
  const response = await fetch(`${api}/api/world/harvest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ building }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? (body as Collected) : (body as Refused);
}

/**
 * Nazwij własną osadę. Raz.
 *
 * Serwer pilnuje i jednej zmiany, i tego, czy to w ogóle osada — tu jest tylko
 * droga do niego. Odpowiedź niesie budynek, więc nazwa na ekranie bierze się
 * z tego, co serwer zapisał, a nie z tego, co zostało wpisane.
 */
export async function renameBuilding(
  api: string,
  token: string,
  building: string,
  name: string
): Promise<Placed | Refused> {
  const response = await fetch(`${api}/api/world/rename`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ building, name }),
  });

  const body = await response.json().catch(() => ({ code: 'broken', message: 'brak odpowiedzi' }));
  return response.ok ? ((body as { building: Placed }).building as Placed) : (body as Refused);
}
